import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

/**
 * Operator-facing error copy keyed by a mutation's `mutationKey`. The trading
 * on/off controls get an explicit "may still be ACTIVE" warning: a silently
 * dropped off-toggle is how a rule the operator believed was off kept trading
 * (Roberto Sánchez opener, 2026-06-09 — the optimistic switch painted OFF but
 * the request never reached the backend and nothing surfaced the failure).
 * Untagged mutations fall back to the generic message.
 */
export const MUTATION_ERROR_MESSAGES: Record<string, string> = {
  // Two failure modes: a precheck/404 abort writes NOTHING, or the rows are all
  // written and only the ENGINE config publish failed. The second is the
  // dangerous one — the UI reads Mongo, so a refresh shows the new values on
  // every row while the engine keeps firing the PREVIOUS config.
  'sniper:bulk-patch-markets':
    'Failed to apply the bulk change — the rows may already be saved while the ENGINE still runs the PREVIOUS config for those markets. Retry until it succeeds; refreshing will NOT show the difference.',
  // Same shape as the bulk write: the row is saved and THEN the engine config is
  // republished, so a failure after the save leaves the engine on the old config
  // while the UI (which reads Mongo) shows the new values.
  'sniper:patch-market':
    'Failed to save the market — the row may already be saved while the ENGINE still runs the PREVIOUS config for that fixture. Retry until it succeeds; refreshing will NOT show the difference.',
  // Worse than a patch: the row is deleted and THEN the config is republished, so
  // a failure after the delete leaves a market the operator can no longer see
  // still present in the engine config — where it can still fire.
  'sniper:delete-market':
    'Failed to delete the market — it may already be REMOVED from the list while the ENGINE still carries it in the previous config and CAN STILL FIRE it. Retry until it succeeds.',
  // The global sniper control (PRED-300). A 409 is a refused transition (illegal
  // from the current state, a stale version, Automático / Prendido while the
  // global halt stands (`code: 'halted'`), or — for Apagado total — a cancel
  // that could not be VERIFIED, including ROOT refusing because the
  // orchestrator is not up to verify it, so nothing was drained; the control
  // bar also shows that sentence inline); a 503 is a transition
  // that applied only PART of its steps. The server's sentence follows this one
  // and names which. The key MUST stay in step with `useSetSniperControl`'s
  // `mutationKey` — `useSniper.keyRegistry.test.ts` pins it.
  'sniper:set-control':
    'El control del sniper NO cambió limpio — puede seguir en el estado ANTERIOR o haber quedado a medias. Releé la barra del control antes de actuar; si el sniper tiene que dejar de operar ya, usá PANIC.',
  // «Sacar halt». Only a 4xx lands here: a 409 wrote nothing (the control is
  // not in Apagado, or a PANIC is running), so the halt STANDS.
  // A 5xx or no answer is indeterminate — see MUTATION_INDETERMINATE_MESSAGES.
  // The server's sentence follows.
  'sniper:clear-halt':
    'No se sacó el halt global del sniper — sigue puesto y no se puede prender. Releé la barra del control antes de reintentar.',
  // Only a panic with NO verdict lands here: an unverified one answers 503 WITH
  // its body and is shown as a result, not as an error. With no answer the
  // cancels may or may not have run.
  'sniper:panic':
    'Sniper PANIC failed with no verdict — orders of the sniper may still be LIVE. Retry the PANIC; if the sniper control plane is down, use the master PANIC ALL in the sidebar.',
  // Mongo and the engine key are kept in agreement by the backend: a rejected key
  // write rolls the change back, and a change that reached the key is kept. So the
  // failure is one of two OPPOSITE outcomes, and only the refetched row says which.
  'sniper:patch-group':
    'Failed to change the group settings — either the previous value was restored (the backend rolls back when the engine key write fails) or the change IS applied and only the engine wake-up was lost. Re-read the row before retrying.',
  // A 503 means the match row took the change but the engine config was not
  // republished, so the engine may still run the previous gate.
  'sniper:patch-event':
    'Failed to change the pre-move gate. Re-read the match before retrying.',
  // The server's own sentence is appended to each of these; it is what says
  // WHY and on which venue (sides contradict the name anchoring, kickoffs
  // disagree, already bound, a market still armed).
  'sniper:genius-bind-preview':
    'Failed to resolve the event(s) against the Genius fixture — NOTHING was bound.',
  'sniper:genius-bind':
    'Failed to bind the Genius fixture — it may or may not be BOUND now, and some of its sniper markets may have been created (always DISARMED). Re-read the Genius tab before retrying.',
  'sniper:genius-unbind':
    'Failed to undo the Genius binding — the fixture may still be BOUND and its sniper markets may still exist. If a market of the fixture is still enabled the backend refuses: disarm it first. Re-read the Genius tab before retrying.',
  // The server ANSWERED with a refusal, so THIS attempt wrote nothing (an
  // earlier one with an unknown outcome is the row's to report). Its own
  // sentence follows and says which of
  // the causes it was. A failure with NO such answer is a different message
  // entirely: see `MUTATION_INDETERMINATE_MESSAGES`.
  'sniper:var-policy-release':
    'Release REFUSED — nothing was sent by this attempt. The row says whether an EARLIER release of this order may still be pending.',
  // Same shape as the release: a server that ANSWERED with a refusal (404, 409
  // `not_flagged`, the 503 that could not read the engine's snapshot) wrote
  // nothing. A failure with NO such answer has its own copy in
  // `MUTATION_INDETERMINATE_MESSAGES`.
  'sniper:rearm':
    'Re-armar REFUSED — nothing was sent by this attempt.',
  // A refusal (armed market, not found) deletes nothing; a partial failure may
  // have removed SOME of the fixture's markets. The server sentence says which.
  'sniper:retire-event':
    'No se pudo retirar el partido — si quedó a medias, algunos de sus mercados ya pueden estar BORRADOS. Revisá la tarjeta antes de reintentar.',
  // The per-match sniper switch. A 409 `engine_lacks_fixture_halt` (the match's
  // engine is too old to stop without a cancel) or a 404 wrote nothing; a 503
  // may have cancelled part of the orders. The server's sentence follows.
  'sniper:disable-event':
    'No se apagó el sniper en el partido — puede seguir OPERÁNDOLO. Releé la tarjeta antes de reintentar.',
  // A 503 after Mongo was switched on leaves the halt key standing: the card
  // then reads ON, with the «Halt colgado» chip only if the key could be read,
  // so the copy must not lean on the chip.
  'sniper:enable-event':
    'No se pudo prender del todo el sniper en el partido: puede figurar ON con el halt todavía puesto, y entonces el motor NO lo opera. Reintentá hasta que salga bien.',
  // A 4xx, or the 503 `data_correction_state_unreadable`, wrote nothing (the
  // 404 `not_downsized` and 409 `latch_changed` resolve, never land here). A
  // failure with NO such answer has its own copy in `MUTATION_INDETERMINATE_MESSAGES`.
  'sniper:data-correction-release':
    'No se volvió a size normal — el partido sigue comprando al size reducido. El motivo sigue.',
  'sniper:data-correction-settings':
    'No se guardó la configuración de corrección de datos de Genius — puede seguir la ANTERIOR. Releela antes de reintentar.',
  // Live events. A removal's own 409/502 come back as results, never here; what
  // lands here with a response is a refusal (a 4xx), which wrote nothing. A
  // failure with NO answer has its own copy in `MUTATION_INDETERMINATE_MESSAGES`.
  'live-events:remove':
    'Removal REFUSED — this attempt removed nothing. The reason follows.',
  // Upsert then activate: a failure between the two leaves the match in the
  // sniper catalog but not activated, which a retry completes.
  'live-events:add':
    'Failed to add the match — it may be in the sniper catalog but NOT activated. Retrying is safe.',
  'entry:create-rule':
    'Failed to create the opener rule — it was NOT created.',
  'entry:activate':
    'Failed to activate the opener rule — it was NOT activated. Retry.',
  'entry:pause':
    'Failed to pause the opener rule — it may still be ACTIVE. Retry and confirm.',
  'entry:set-armed':
    'Failed to change the opener rule on/off — its real state may be unchanged (it could still be ACTIVE). Retry and confirm the switch.',
  'entry:set-maker-flag':
    'Failed to change the opener rule — it may still be ACTIVE. Retry and confirm the switch.',
  'entry:bulk-armed':
    'Failed to arm/disarm the selected opener rules — their real state may be unchanged (some could still be ACTIVE, others still paused). Retry and confirm.',
  'entry:bulk-set-maker-flag':
    'Failed to change the maker flag for the selected opener rules — their real state may be unchanged (some could still be ACTIVE). Retry and confirm.',
  'entry:toggle-platform':
    'Failed to toggle the opener platform — the rule may still be ACTIVE. Retry and confirm the switch.',
  'entry:update-rule-params':
    'Failed to save the opener rule parameters — the running rule may still use the OLD values. Retry and confirm.',
  'entry:bulk-params':
    'Failed to apply the bulk parameters — the opener rules may still use the OLD values. Retry.',
  'entry:set-pair-opener-enabled':
    'Failed to change the pair market data on/off — its real state may be unchanged (it could still be streaming). Retry and confirm the switch.',
  'entry:bulk-opener-enabled':
    'Failed to change market data for the pairs — their real state may be unchanged (some could still be ON, others still OFF). Retry and confirm.',
  'unwinding:pause':
    'Failed to pause the unwinder rule — it may still be ACTIVE. Retry and confirm the switch.',
  'unwinding:toggle-platform':
    'Failed to toggle the unwinder platform — the rule may still be ACTIVE. Retry and confirm the switch.',
  'unwinding:update-rule-params':
    'Failed to save the unwinder rule parameters — the running rule may still use the OLD values. Retry and confirm.',
  'unwinding:bulk-params':
    'Failed to apply the bulk parameters — the unwinder rules may still use the OLD values. Retry.',
  'unwinding:set-pair-unwinder-enabled':
    'Failed to change the pair market data on/off — its real state may be unchanged (it could still be monitored). Retry and confirm the switch.',
  'unwinding:bulk-unwinder-enabled':
    'Failed to change market data for the pairs — their real state may be unchanged (some could still be ON, others still OFF). Retry and confirm.',
  // Only the HTTP dispatch of these two lands here — a job that starts and
  // then fails a step reports through the job stepper/snackbar instead.
  'entry:start-opener':
    'Failed to start the opener — the request never reached the control plane, so NOTHING was started. Retry.',
  'entry:stop-opener':
    'Failed to stop the opener — the request never reached the control plane, so orders may still be RESTING and rules ACTIVE. Retry immediately.',
  'entry:clear-panic-lockout':
    'Failed to clear the 24h lockout — it is probably STILL armed, so starting a group or activating a rule will keep being refused. Retry and confirm.',
  'entry:group-start':
    'Failed to start the group pods — the group is NOT running.',
  'entry:group-stop':
    'Failed to stop the group — its rules may still be ACTIVE and its pods may still be running.',
  'entry:group-panic-soft':
    'Group SOFT PANIC failed — orders may still be resting and rules may still be ACTIVE.',
  'entry:group-panic':
    'Group HARD PANIC failed — the engine pod may still be running and orders may still be resting.',
  'entry:group-finish':
    'Failed to finish group — the group may NOT be finished. Retry.',
  'entry:archive-finished':
    'Failed to archive the finished pairs — NOTHING was archived, the group table is unchanged. Retry.',
  // Per-rule refusals are CAUGHT server-side and come back as a 200 carrying
  // `skipped` (`rules.service.ts::resumeForfaitEventMatch`), so an error here
  // is the request itself failing: a 404 (the matchKey resolves to no tennis
  // pair — likely on an archived card whose pairs are gone) or a 400, and both
  // write NOTHING. Only a transport failure mid-flight can leave it part-way.
  'entry:resume-forfait-match':
    'Failed to resume the match. If the match no longer has tennis pairs (404) NOTHING was written; otherwise some rules may have been re-armed and others may still be SUSPENDED — check the Forf badges before retrying.',
  'match-events:readopt':
    'Could not re-adopt the Genius score — the match baseline did NOT change. Retry.',
  'positions:toggle-tna-alerts':
    'Failed to toggle TNA alerts for this position — the setting may not have changed. Retry.',
  'entry:create-subgroup':
    'Failed to create the subgroup — it was NOT created.',
  'entry:update-subgroup':
    'Failed to save the subgroup — its config snapshot was NOT changed.',
  'entry:delete-subgroup':
    'Failed to delete the subgroup — its members may still be assigned.',
  'entry:assign-pair-subgroup':
    'Failed to move the pair to the subgroup — its assignment is unchanged.',
  'entry:bulk-assign-subgroup':
    'Failed to move the selected pairs — their subgroup assignment is unchanged.',
  'entry:apply-subgroup':
    'Failed to apply the subgroup config — member rules may NOT carry the snapshot. Retry.',
  'entry:create-subgroup-template':
    'Failed to create the template — it was NOT created.',
  'entry:update-subgroup-template':
    'Failed to save the template — it was NOT changed.',
  'entry:delete-subgroup-template':
    'Failed to delete the template — it may still exist.',
  'entry:instantiate-subgroup-template':
    'Failed to instantiate the template — the group subgroups may NOT have been created.',
  'entry:save-group-default':
    'Failed to save the group default config — no snapshot was stored. Retry.',
  'entry:reset-group-default':
    'Failed to reset rules to the group default — member rules were NOT changed. Retry.',
  // The keys may or may not have been cleared before the failure — they are
  // cleared BEFORE the bookkeeping that can throw — so the message points at the
  // chips that read the real state instead of guessing. And it says NOTHING
  // about the rules: this one key is shared by every branch of the control,
  // including the ORPHANED halt, where no panic ran and the rules may well be
  // armed.
  // The switch that decides whether the NEXT maintenance window stops the
  // platform. It promises nothing about the window on screen: the decision is
  // latched at its deadline, so a failed change may or may not have mattered
  // — what the operator has to do is look at the bar, not infer from a toast.
  'system:venue-maintenance-auto-panic':
    'The auto-panic switch was NOT changed — it is still whatever the maintenance bar shows. If a window is counting down, the OLD setting is what decides it. Retry and confirm on the bar.',
  'system:venue-maintenance-resume':
    'Lift-halt request FAILED — the halt may or may not be cleared. Check the halt chips for what is actually armed.',
  // A failed PANIC ALL is the worst possible silence: the operator asked for a
  // full halt and may have got a PARTIAL one (some domains armed, others not).
  'system:panic-all':
    'PANIC ALL failed — the halt may have applied to only SOME domains, and rules elsewhere can still be ACTIVE. Retry immediately and check each domain.',
  'opener:boot-orchestrator':
    'Failed to boot the opener control plane — it is still DOWN, so opener rule changes will not reach the engine.',
  'opener:shutdown-orchestrator':
    'Failed to shut down the opener control plane — it may still be running.',
  'unwinder:boot-orchestrator':
    'Failed to boot the unwinder control plane — it is still DOWN, so unwinder rule changes will not reach the engine.',
  'unwinder:shutdown-orchestrator':
    'Failed to shut down the unwinder control plane — it may still be running.',
  // A drain refusal reaches here as a bare `new Error(reason)` with no axios
  // `response`, so `extractServerErrorMessage` finds nothing and the global
  // toast falls back to the generic copy — while the button's own snackbar shows
  // the real reason. Two surfaces, and without these the louder one says less.
  'matching:boot-orchestrator':
    'Failed to boot the matcher control plane — it is still DOWN, so the matching runs, schedules and pair confirm/reject will keep failing.',
  'matching:shutdown-orchestrator':
    'Failed to shut down the matcher control plane — it may still be running. The backend REFUSES the drain while any matcher schedule is armed, or when it could not read their state.',
  'opportunities:boot-orchestrator':
    'Failed to boot the pair-catalog control plane — it is still DOWN, so the pair list and its counters will keep failing to load.',
  'opportunities:shutdown-orchestrator':
    'Failed to shut down the pair-catalog control plane — it may still be running. The backend REFUSES the drain while the opportunities domain is enabled, unseeded or unreadable.',
  'entry:soccer-matches-prepare':
    'Failed to prepare the soccer match — its buckets may be PARTIALLY assigned/enabled/ruled. Re-open the drawer and check the status chips before retrying.',
  'entry:soccer-matches-turn-on':
    'Failed to turn on the match — some buckets may be PARTIALLY prepared/armed while others were not reached. If a pair ALREADY had a rule, its maker flags may now be in an in-between state that the status chips (based on counts, not flags) will NOT show as broken. Re-open the drawer, but also check that pair\'s maker flags directly on the Entry table before retrying.',
  'entry:soccer-matches-turn-off':
    'Failed to turn off the match — some rules may still be ARMED, and a rule\'s maker flags may now be PARTIALLY cleared, a state the status chips (based on counts, not flags) will NOT show as broken. Re-open the drawer, but also check that rule\'s maker flags directly on the Entry table before retrying.',
  // Provider-neutral on purpose: ONE mutation key serves every provider's
  // confirm, so naming one of them here was already wrong for the other.
  'match-external-refs:confirm':
    'Failed to bind the fixture — the match may still have NO external live-signal binding. Retry.',
  'match-external-refs:acting':
    'Failed to change "Activar en vivo" — the framework may still be using the PREVIOUS acting state for this fixture. Retry and confirm the switch.'
};

/**
 * Copy for a failure whose OUTCOME is unknown: the request may have been
 * carried out even though no answer came back. Only for writes where assuming
 * "it did not happen" is the dangerous reading.
 */
export const MUTATION_INDETERMINATE_MESSAGES: Record<string, string> = {
  // The control plane writes the command (1 h TTL) and THEN answers. A timeout
  // or a gateway error after that write means the command exists and the
  // engine will honour it.
  'sniper:var-policy-release':
    'Release outcome UNKNOWN — the release may have been registered and can be honoured by the engine for up to 1 h. Do NOT assume it was not sent. Re-issuing it is safe.',
  // Written (1 h TTL) and THEN answered, like the release: no answer, or the
  // 503 `rearm_outcome_unknown`, can mean the command exists.
  'sniper:rearm':
    'Re-armar outcome UNKNOWN — it may have been registered and can be honoured by the engine for up to 1 h; the chip clears once it is. Re-issuing it is safe.',
  // Four steps run in order (sniper, catalog, opener binding, list); without an
  // answer any prefix of them may be done. Each is idempotent.
  'live-events:remove':
    'Removal outcome UNKNOWN — some of its steps (sniper, catalog, opener binding, live list) may already be done. Every step is idempotent: retrying is safe.',
  // «Sacar halt»: only a 4xx is definite. A 5xx (the backend can answer 503
  // after the DEL landed, or a gateway 502 in front of it) or no answer at all
  // may come after the halt was already lifted.
  'sniper:clear-halt':
    'No sabemos si se sacó el halt — mirá la barra antes de volver a intentar',
  // Written and THEN answered: no answer, the 503
  // `data_correction_release_outcome_unknown`, or a 5xx with no code can mean
  // the partido is already back at normal size. A repeat answers 404.
  'sniper:data-correction-release':
    'No sabemos si se volvió a size normal: puede haberse registrado. Mirá el chip de la tarjeta antes de reintentar; reintentar es seguro.',
};

/**
 * Keys whose route has no nothing-written 5xx: every 5xx is indeterminate, and
 * only a 4xx is a definite refusal.
 */
const INDETERMINATE_ON_EVERY_5XX: ReadonlySet<string> = new Set(['sniper:clear-halt']);

/**
 * The one 503 the release route answers BEFORE it writes anything: it could not
 * read (or parse) the engine's state. Its other coded 503,
 * `release_outcome_unknown`, is the Redis `SET` not confirming — a client-side
 * command timeout can reject a write that was applied.
 */
const RELEASE_NOTHING_WRITTEN_503_CODE = 'frozen_state_unreadable';

/**
 * Per mutation, the 503 `code` its route answers BEFORE writing anything. The
 * re-arm reads the engine's snapshot first, as the release does, and refuses
 * with the same code when it cannot; its `rearm_outcome_unknown` is the
 * unconfirmed write. «Volver a size normal» refuses with
 * `data_correction_state_unreadable` when it cannot read the latch; its
 * `data_correction_release_outcome_unknown` is the unconfirmed write.
 */
const NOTHING_WRITTEN_503_CODES: Record<string, string> = {
  'sniper:var-policy-release': RELEASE_NOTHING_WRITTEN_503_CODE,
  'sniper:rearm': 'frozen_state_unreadable',
  'sniper:data-correction-release': 'data_correction_state_unreadable',
};

/**
 * Whether a failed write (release, re-arm, «Sacar halt») may nonetheless have
 * been applied.
 *
 * Classified by the response's stable `code`, never by its prose. DETERMINATE
 * (refused, nothing written): any 4xx, or a 503 with the mutation's
 * nothing-written code ({@link NOTHING_WRITTEN_503_CODES}) — never for a key in
 * {@link INDETERMINATE_ON_EVERY_5XX}. Everything else is
 * indeterminate — no response at all (timeout, network error), a 503 with
 * another code or with none, or a 5xx that can come from a gateway in front of
 * a server that already did the work.
 */
export function isIndeterminateFailure(
  error: unknown,
  mutationKey: string = 'sniper:var-policy-release',
): boolean {
  const status = (error as { response?: { status?: unknown } } | null | undefined)?.response
    ?.status;
  if (typeof status !== 'number') return true;
  if (status >= 400 && status < 500) return false;
  if (INDETERMINATE_ON_EVERY_5XX.has(mutationKey)) return true;
  const code = (error as { response?: { data?: { code?: unknown } | null } }).response?.data?.code;
  const nothingWritten = NOTHING_WRITTEN_503_CODES[mutationKey] ?? RELEASE_NOTHING_WRITTEN_503_CODE;
  return !(status === 503 && code === nothingWritten);
}

/**
 * A write refused in the browser before any request left it. Its own message
 * says why, and nothing was sent, so neither the keyed copy nor the
 * indeterminate one applies.
 */
export class ClientRefusalError extends Error {
  readonly clientRefusal = true as const;

  constructor(message: string) {
    super(message);
    this.name = 'ClientRefusalError';
  }
}

export function isClientRefusal(error: unknown): error is ClientRefusalError {
  if (!error || typeof error !== 'object') return false;
  const e = error as { clientRefusal?: unknown; response?: unknown; message?: unknown };
  return (
    e.clientRefusal === true &&
    e.response === undefined &&
    typeof e.message === 'string' &&
    e.message.trim().length > 0
  );
}

export const DEFAULT_MUTATION_ERROR_MESSAGE =
  'Action failed — server state may be unchanged. Please retry.';

/**
 * Message-stem routing for the groups-only trading rejection. Activation
 * mutations (`entry:set-maker-flag`, `entry:toggle-platform`) are tagged
 * with the "may still be ACTIVE" copy — but when the backend refused the
 * activation BECAUSE the pair has no opener group, that copy is wrong (the
 * rule is definitively NOT active) and unactionable. Matching on the stable
 * stem of the backend message (not the full phrasing, which may drift)
 * routes this case to copy that tells the operator exactly what to do.
 */
export const GROUPS_ONLY_REJECTION_PATTERN =
  /not assigned to an opener group/i;

export const GROUPS_ONLY_REJECTION_MESSAGE =
  'Rule NOT activated — the pair is not assigned to an opener group; assign it in the Pairs Bucket and retry.';

/**
 * Best-effort extraction of the backend's rejection reason from an axios
 * error (NestJS error body: `message` as a string, or string[] from the
 * ValidationPipe). The generic toast used to swallow these — a precise 400
 * like "thresholdSum must be < 1.0" surfaced as an opaque "Action failed",
 * leaving the operator guessing which input the server refused.
 */
export function extractServerErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | null | undefined)
    ?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const message = (data as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const parts = message.filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0
    );
    if (parts.length) return parts.join('; ');
  }
  return null;
}

export function resolveMutationErrorMessage(
  mutationKey: unknown,
  error?: unknown
): string {
  if (isClientRefusal(error)) return error.message;
  const serverMessage = extractServerErrorMessage(error);
  // Groups-only activation rejection overrides BOTH the keyed copy and the
  // generic fallback: the keyed "may still be ACTIVE" warning would mislead
  // here — the backend refused the activation, so the rule is NOT active.
  if (serverMessage && GROUPS_ONLY_REJECTION_PATTERN.test(serverMessage)) {
    return GROUPS_ONLY_REJECTION_MESSAGE;
  }
  if (
    typeof mutationKey === 'string' &&
    error !== undefined &&
    Object.prototype.hasOwnProperty.call(MUTATION_INDETERMINATE_MESSAGES, mutationKey) &&
    isIndeterminateFailure(error, mutationKey)
  ) {
    const unknown = MUTATION_INDETERMINATE_MESSAGES[mutationKey];
    return serverMessage ? `${unknown} ${serverMessage}` : unknown;
  }
  if (
    typeof mutationKey === 'string' &&
    Object.prototype.hasOwnProperty.call(MUTATION_ERROR_MESSAGES, mutationKey)
  ) {
    const tailored = MUTATION_ERROR_MESSAGES[mutationKey];
    return serverMessage ? `${tailored} ${serverMessage}` : tailored;
  }
  return serverMessage
    ? `Action failed — ${serverMessage}`
    : DEFAULT_MUTATION_ERROR_MESSAGE;
}

/**
 * Single source of truth for surfacing a failed react-query mutation to the
 * operator. Wired app-wide via the QueryClient's MutationCache so NO mutation
 * failure is ever silent — the optimistic-toggle rollback reverts the switch,
 * this makes the failure visible and reports it to Sentry.
 */
export function handleMutationError(error: unknown, mutationKey?: unknown): void {
  toast(resolveMutationErrorMessage(mutationKey, error), {
    theme: 'dark',
    type: 'error',
    autoClose: 8000,
    pauseOnFocusLoss: false
  });
  Sentry.captureException(error, {
    extra: {
      mutationKey: typeof mutationKey === 'string' ? mutationKey : null,
      serverMessage: extractServerErrorMessage(error)
    }
  });
}
